// index.js
import 'babel-polyfill';
import React from 'react';
import ReactDOM from 'react-dom';
import { SolrClient } from './solr-faceted-search-react/src/index';
import FederatedSolrComponentPack from './components/federated_solr_component_pack';
import FederatedSolrFacetedSearch from './components/federated-solr-faceted-search';
import helpers from './helpers';
import reactTInit from './components/reactT/reactTInit';
import reactT from './components/reactT';
// import search app boilerplate styles
import './styles.css';

/**
 * Executes search query based on the value of URL querystring params.
 *
 * @param solrClient
 *   Instantiated solrClient.
 * @param options
 *   Config options, used to determine initial site search name
 */
const searchFromQuerystring = (solrClient, options = {}) => {
  // Get existing querystring params.
  const { parsed, params } = helpers.qs.getParsedQsAndParams();

  let searchFieldsState = solrClient.state.query.searchFields;

  // Set the state for searchFields based on qs params.
  searchFieldsState.forEach((searchField) => {
    // Get the field machine name for the main query field.
    if (Object.prototype.hasOwnProperty.call(options, 'mainQueryField') && searchField.field === options.mainQueryField) {
      // Set the state of the main query field to the value of the search qs param
      searchField.value = parsed.search;
    }
    // If the searchField is one for which we preserve state through qs.
    if (helpers.filterFieldsWithQsState.find((filterField) => filterField === searchField.field)) {
      searchField = helpers.qs.setFieldStateFromQs({
        params,
        searchField
      }) // this resets our initial state of search sites
    }
    // If restricted to the current site by configuration, enforce it here.
    // This rule only applies if site has not been selected by the user.
    if (options.siteSearch !== undefined && searchField.field === 'sm_site_name' && searchField.value === undefined && searchField.isHidden === false) {
      searchField.value = [options.siteSearch];
    }
  });
  // Ensure the initial query succeeds by setting a default start value.
  solrClient.state.query.start = solrClient.state.query.start || 0;
};

/**
 * Gets the category mapping array.
 *
 * @returns object
 *   An object that contains content type with its category field:
 */
function getCategoryMapping() {
  return {
    "publication": "ss_publication_category",
    "event": "ss_event_category",
    "news": "ss_news_category",
    "supporters": "ss_supporters_category",
  };
}

/**
 * Gets the tags mapping array.
 *
 * @returns object
 *   An object that contains content type with its tags field:
 */
function getTagsMapping() {
  return {
    "news": "sm_tags",
    "publication": "sm_tags",
    "event": "sm_tags",
    "supporters": "sm_tags",
  };
}

/**
 * Updates search query based on the drupalSettings.filter if exists.
 *
 * @param solrClient
 *   Instantiated solrClient.
 * @param options
 *   Config options, used to determine initial site search name
 */
const categoryDefaultValue = (solrClient, options = {}) => {
  // Get existing querystring params.
  const { parsed, params } = helpers.qs.getParsedQsAndParams();

  let searchFieldsState = solrClient.state.query.searchFields;
  // Set the state for searchFields based on qs params.
  searchFieldsState.forEach((searchField) => {
    // Get the field machine name for the main query field.
    if (Object.prototype.hasOwnProperty.call(options, 'mainQueryField') && searchField.field === options.mainQueryField) {
      // Set the state of the main query field to the value of the search qs param
      searchField.value = parsed.search;
    }
    // If the searchField is one for which we preserve state through qs.
    if (helpers.filterFieldsWithQsState.find((filterField) => filterField === searchField.field)) {
      searchField = helpers.qs.setFieldStateFromQs({
        params,
        searchField
      }) // this resets our initial state of search sites
    }
    // If restricted to the current site by configuration, enforce it here.
    // This rule only applies if site has not been selected by the user.
    if (typeof drupalSettings !== 'undefined' && typeof drupalSettings.filter !== 'undefined') {
      const categoryMapping = getCategoryMapping();
      if (searchField.field === categoryMapping[drupalSettings.type] && searchField.isHidden === false) {
        if (!Array.isArray(searchField.value) || !searchField.value.length) {
          searchField.value = [drupalSettings.filter];
        }
      }
    }

    if (typeof drupalSettings !== 'undefined' && typeof drupalSettings.sm_tags !== 'undefined') {
      const tagsMapping = getTagsMapping();
      if (searchField.field === tagsMapping[drupalSettings.type] && searchField.isHidden === false) {
        if (!Array.isArray(searchField.value) || !searchField.value.length) {
          searchField.value = [drupalSettings.sm_tags];
        }
      }
    }

    if (typeof drupalSettings !== 'undefined' && typeof drupalSettings.tags_op !== 'undefined') {
      if (searchField.field === 'tags_op' && searchField.isHidden === false) {
        if (!Array.isArray(searchField.value) || !searchField.value.length) {
          searchField.value = [drupalSettings.tags_op];
        }
      }
    }
  });
};

// Initialize the solr client + search app with settings.
const init = (settings) => {
  reactTInit();
  const langcode = document.querySelector('html').getAttribute('content-lang');
  const queryParams = new URLSearchParams(window.location.search)
  let keywordsValue;

  if (queryParams.get("search") && queryParams.get("search").length > 0) {
    keywordsValue = queryParams.get("search");
  }
  else {
    keywordsValue = '';
  }

  // Default field for publication category
  let publicationCategoryField = "sm_publication_media";
  let publicationCategoryId = "publication_media";
  if (typeof drupalSettings.publication_content_types !== 'undefined') {
    if (drupalSettings.publication_content_types === 'PDF publications only') {
      publicationCategoryField = "ss_publication_media_name";
    } else if (drupalSettings.publication_content_types === 'HTML publication pages only') {
      publicationCategoryField = "ss_publication_category";
      publicationCategoryId = "publication_category";
    }
  }

  let defaults = {
    isD7: false,
    // Whether or not we should be querying the solr backend directly.
    proxyIsDisabled: false,
    // The query request endpoint url must be assigned in ./.env.local.js and by the search app settings in the module.
    url: "",
    // The search fields and filterable facets.
    searchFields: [
      { label: reactT('Enter Search Term') + ':', field: '*', type: "text", isHidden: false, value: keywordsValue, id: "textSearchField" },
      { label: reactT('Search across sites'), field: "sm_site_name", type: "list-facet", collapse: false, isHidden: false, id: 'site_name', showAllTitle: reactT('Site names') },
      { label: reactT('Type'), field: "ss_type", type: "list-facet", collapse: false, isHidden: true, id: 'type', showAllTitle: reactT('Types') },
      { label: reactT('Categories'), field: "ss_field_categories_list", type: "list-facet", collapse: false, isHidden: false, id: 'categories', showAllTitle: reactT('Categories') },
      { label: reactT('Topic'), field: "sm_topics", type: "list-facet", collapse: false, isHidden: false, id: 'topics', showAllTitle: reactT('Topics') },
      { label: reactT('Tag'), field: "sm_tags", type: "list-facet", collapse: false, isHidden: false, id: 'tag', showAllTitle: reactT('Tags') },
      { label: reactT('Keyword'), field: "sm_keywords", type: "list-facet", collapse: false, isHidden: false, id: 'keyword', showAllTitle: reactT('Keywords') },
      { label: reactT('Document type'), field: "ss_document_type", type: "list-facet", collapse: false, isHidden: false, id: 'document_type', showAllTitle: reactT('Document types') },
      { label: reactT('Publication category'), field: publicationCategoryField, type: "list-facet", collapse: false, isHidden: false, id: publicationCategoryId, showAllTitle: reactT('Publication categories') },
      { label: reactT('Event category'), field: "sm_event_category_media", type: "list-facet", collapse: false, isHidden: false, id: 'event_category_media', showAllTitle: reactT('Event categories') },
      { label: reactT('Document country'), field: "sm_countries_media", type: "list-facet", collapse: false, isHidden: false, id: 'countries_media', showAllTitle: reactT('Document countries') },
      { label: reactT("Date"), field: "ds_created", type: "range-facet", collapse: false, isHidden: false },
      { label: reactT("Language"), field: "ss_search_api_language", type: "text", collapse: false, isHidden: true, value: [langcode] },
    ],
    // The solr field to use as the source for the main query param "q".
    mainQueryField: "*",
    sortFields: [
      { label: reactT("Relevance"), field: "score" },
      { label: reactT("Date"), field: "ds_created" },
    ],
    // Enable highlighting in search results snippets.
    hl: {
      fl: `tm_X3b_${langcode}_rendered_item`, // the highlight snippet source field(s)
      usePhraseHighlighter: true // highlight phrase queries
    },
    pageStrategy: "paginate",
    rows: 10,
    // Hostname overridable in ./.env.local.js for testing purposes.
    hostname: window.location.hostname,
    autocomplete: false,
  };

  let search_type = '';
  if (typeof drupalSettings !== 'undefined') {
    search_type = drupalSettings.type;
    let is_hidden = true;

    // Default field for publication category
    let publicationCategoryField = "sm_publication_media";
    let publicationCategoryId = "publication_media";
    if (typeof drupalSettings.publication_content_types !== 'undefined') {
      if (drupalSettings.publication_content_types === 'PDF publications only') {
        publicationCategoryField = "ss_publication_media_name";
      } else if (drupalSettings.publication_content_types === 'HTML publication pages only') {
        publicationCategoryField = "ss_publication_category";
        publicationCategoryId = "publication_category";
      }
    }

    /*global drupalSettings:true*/

    // Add filters based on search type.
    switch (search_type) {
      case 'publication':
        if (typeof drupalSettings.tags_op !== 'undefined') {
          is_hidden = false;
        }
        defaults['searchFields'] = [
          { label: reactT('Enter Search Term') + ':', field: '*', type: "text", isHidden: false, value: keywordsValue, id: "textSearchField" },
          { label: reactT('Site name'), field: "sm_site_name", type: "list-facet", collapse: false, isHidden: true, id: 'site_name', showAllTitle: reactT('Site names') },
          { label: reactT('Publication category'), field: publicationCategoryField, type: "list-facet", collapse: false, isHidden: false, id: publicationCategoryId, showAllTitle: reactT('Publication categories') },
          { label: reactT('Topic'), field: "sm_topics", type: "list-facet", collapse: false, isHidden: false, id: 'topics', showAllTitle: reactT('Topics') },
          { label: reactT('Tag'), field: "sm_tags", type: "list-facet", collapse: false, isHidden: false, id: 'tag', showAllTitle: reactT('Tags') },
          { label: reactT('Type'), field: "ss_type", type: "list-facet", collapse: false, isHidden: true, value: ['publication'], id: 'type', showAllTitle: reactT('Types') },
          { label: reactT('Region'), field: "sm_region", type: "list-facet", collapse: false, isHidden: false, id: 'region', showAllTitle: reactT('Regions') },
          { label: reactT('Country'), field: "sm_countries", type: "list-facet", collapse: false, isHidden: false, id: 'country', showAllTitle: reactT('Countries') },
          { label: reactT("Date"), field: "ds_created", type: "range-facet", collapse: false, isHidden: false },
          { label: reactT("Language"), field: "ss_search_api_language", type: "text", collapse: false, isHidden: true, value: [langcode] },
          { label: reactT("tags_op"), field: "tags_op", type: "list-facet", collapse: false, isHidden: is_hidden },
        ];
        break;

      case 'event':
        if (typeof drupalSettings.tags_op !== 'undefined') {
          is_hidden = false;
        }
        defaults['searchFields'] = [
          { label: reactT('Enter Search Term') + ':', field: '*', type: "text", isHidden: false, value: keywordsValue, id: "textSearchField" },
          { label: reactT('Site name'), field: "sm_site_name", type: "list-facet", collapse: false, isHidden: true, id: 'site_name', showAllTitle: reactT('Site names') },
          { label: reactT('Event category'), field: "ss_event_category", type: "list-facet", collapse: false, isHidden: false, id: 'event_category', showAllTitle: reactT('Event categories') },
          { label: reactT('Type'), field: "ss_type", type: "list-facet", collapse: false, isHidden: true, value: ['event'], id: 'type', showAllTitle: reactT('Types') },
          { label: reactT('Tag'), field: "sm_tags", type: "list-facet", collapse: false, isHidden: false, id: 'tag', showAllTitle: reactT('Tags') },
          { label: reactT('Event type'), field: "ss_field_event_type", type: "list-facet", collapse: false, isHidden: false, id: 'event_type', showAllTitle: reactT('Event types') },
          { label: reactT('Region'), field: "sm_region", type: "list-facet", collapse: false, isHidden: false, id: 'region', showAllTitle: reactT('Regions') },
          { label: reactT('Country'), field: "sm_countries", type: "list-facet", collapse: false, isHidden: false, id: 'country', showAllTitle: reactT('Countries') },
          { label: reactT("Date"), field: "ds_field_date", type: "range-facet", collapse: false, isHidden: false },
          { label: reactT("Language"), field: "ss_search_api_language", type: "text", collapse: false, isHidden: true, value: [langcode] },
          { label: reactT("tags_op"), field: "tags_op", type: "list-facet", collapse: false, isHidden: is_hidden },
        ];
        // Filter by event date field.
        defaults['sortFields'] = [
          { label: reactT("Relevance"), field: "score" },
          { label: reactT("Date"), field: "ds_field_date" },
        ];
        break;

      case 'news':
        if (typeof drupalSettings.tags_op !== 'undefined') {
          is_hidden = false;
        }
        defaults['searchFields'] = [
          { label: reactT('Enter Search Term') + ':', field: '*', type: "text", isHidden: false, value: keywordsValue, id: "textSearchField" },
          { label: reactT('Site name'), field: "sm_site_name", type: "list-facet", collapse: false, isHidden: true, id: 'site_name', showAllTitle: reactT('Site names') },
          { label: reactT('News category'), field: "ss_news_category", type: "list-facet", collapse: false, isHidden: false, id: 'news_category', showAllTitle: reactT('News categories') },
          { label: reactT('Tag'), field: "sm_tags", type: "list-facet", collapse: false, isHidden: false, id: 'tag', showAllTitle: reactT('Tags') },
          { label: reactT('Type'), field: "ss_type", type: "list-facet", collapse: false, isHidden: true, value: ['news'], id: 'type', showAllTitle: reactT('Types') },
          { label: reactT('Topic'), field: "sm_topics", type: "list-facet", collapse: false, isHidden: false, id: 'topics', showAllTitle: reactT('Topics') },
          { label: reactT('Region'), field: "sm_region", type: "list-facet", collapse: false, isHidden: false, id: 'region', showAllTitle: reactT('Regions') },
          { label: reactT('Country'), field: "sm_countries", type: "list-facet", collapse: false, isHidden: false, id: 'country', showAllTitle: reactT('Countries') },
          { label: reactT("Date"), field: "ds_created", type: "range-facet", collapse: false, isHidden: false },
          { label: reactT("Language"), field: "ss_search_api_language", type: "text", collapse: false, isHidden: true, value: [langcode] },
          { label: reactT("tags_op"), field: "tags_op", type: "list-facet", collapse: false, isHidden: is_hidden },
        ];
        break;

      case 'supporters':
        if (typeof drupalSettings.tags_op !== 'undefined') {
          is_hidden = false;
        }
        defaults['searchFields'] = [
          { label: reactT('Enter Search Term') + ':', field: '*', type: "text", isHidden: false, value: keywordsValue, id: "textSearchField" },
          { label: reactT('Site name'), field: "sm_site_name", type: "list-facet", collapse: false, isHidden: true, id: 'site_name', showAllTitle: reactT('Site names') },
          { label: reactT('Supporter category'), field: "ss_supporters_category", type: "list-facet", collapse: false, isHidden: false, id: 'supporters_category', showAllTitle: reactT('Supporter categories') },
          { label: reactT('Type'), field: "ss_type", type: "list-facet", collapse: false, isHidden: true, value: ['prominent_supporter'], id: 'type', showAllTitle: reactT('Types') },
          { label: reactT('Tag'), field: "sm_tags", type: "list-facet", collapse: false, isHidden: false, id: 'tag', showAllTitle: reactT('Tags') },
          { label: reactT('Region'), field: "sm_region", type: "list-facet", collapse: false, isHidden: false, id: 'region', showAllTitle: reactT('Regions') },
          { label: reactT('Country'), field: "sm_countries", type: "list-facet", collapse: false, isHidden: false, id: 'country', showAllTitle: reactT('Countries') },
          { label: reactT("Date"), field: "ds_created", type: "range-facet", collapse: false, isHidden: false },
          { label: reactT("Language"), field: "ss_search_api_language", type: "text", collapse: false, isHidden: true, value: [langcode] },
          { label: reactT("tags_op"), field: "tags_op", type: "list-facet", collapse: false, isHidden: is_hidden },
        ];
        break;

      default:
        defaults['searchFields'] = [
          { label: reactT('Enter Search Term') + ':', field: '*', type: "text", isHidden: false, value: keywordsValue, id: "textSearchField" },
          { label: reactT('Search across sites'), field: "sm_site_name", type: "list-facet", collapse: false, isHidden: false, id: 'site_name', showAllTitle: reactT('Site names') },
          { label: reactT('Type'), field: "ss_type", type: "list-facet", collapse: false, isHidden: true, id: 'type', showAllTitle: reactT('Types') },
          { label: reactT('Categories'), field: "ss_field_categories_list", type: "list-facet", collapse: false, isHidden: false, id: 'categories', showAllTitle: reactT('Categories') },
          { label: reactT('Topic'), field: "sm_topics", type: "list-facet", collapse: false, isHidden: false, id: 'topics', showAllTitle: reactT('Topics') },
          { label: reactT('Tag'), field: "sm_tags", type: "list-facet", collapse: false, isHidden: false, id: 'tag', showAllTitle: reactT('Tags') },
          { label: reactT('Keyword'), field: "sm_keywords", type: "list-facet", collapse: false, isHidden: false, id: 'keyword', showAllTitle: reactT('Keywords') },
          { label: reactT('Document type'), field: "ss_document_type", type: "list-facet", collapse: false, isHidden: false, id: 'document_type', showAllTitle: reactT('Document types') },
          { label: reactT('Publication category'), field: publicationCategoryField, type: "list-facet", collapse: false, isHidden: false, id: publicationCategoryId, showAllTitle: reactT('Publication categories') },
          { label: reactT('Event category'), field: "sm_event_category_media", type: "list-facet", collapse: false, isHidden: false, id: 'event_category_media', showAllTitle: reactT('Event categories') },
          { label: reactT('Document country'), field: "sm_countries_media", type: "list-facet", collapse: false, isHidden: false, id: 'countries_media', showAllTitle: reactT('Document countries') },
          { label: reactT("Date"), field: "ds_created", type: "range-facet", collapse: false, isHidden: false },
          { label: reactT("Language"), field: "ss_search_api_language", type: "text", collapse: false, isHidden: true, value: [langcode] },
          { label: reactT("tags_op"), field: "tags_op", type: "list-facet", collapse: false, isHidden: true },
        ];
        break;

    }

    // Add pages per page based on search type.
    if (search_type === 'main') {
      defaults['rows'] = 10;
    }
    else {
      defaults['rows'] = 9;
    }
  }

  const options = Object.assign(defaults, settings);

  // Update searchFields to indicate which facets or filters should be hidden in the UI.
  // Note: these facets and filters may still be used in the query.
  settings.hiddenSearchFields = settings.hiddenSearchFields || [];
  options.searchFields = options.searchFields.map(searchField => {
    if (settings.hiddenSearchFields.includes(searchField.field)) {
      searchField.isHidden = true;
    }
    return searchField;
  });

  // Set sm_site_name default values from config
  const sm_site_name_value = settings.sm_site_name || false;

  // Logic for restricting site search based on config.
  options.siteList = [];
  if (settings.siteSearch !== undefined) {
    options.searchFields.forEach((searchField) => {
      if (searchField.field === 'sm_site_name' &&
        (searchField.isHidden || sm_site_name_value.length < 2)) {
        options.siteList = [settings.siteSearch];
      }
    });
  }
  else {
    options.siteList = sm_site_name_value;
  }
  // The client class.
  const solrClient = new SolrClient({
    isD7: options.isD7,
    proxyIsDisabled: options.proxyIsDisabled,
    url: options.url,
    userpass: options.userpass,
    searchFields: options.searchFields,
    sortFields: options.sortFields,
    pageStrategy: options.pageStrategy,
    rows: options.rows,
    hl: options.hl,
    mainQueryField: options.mainQueryField,
    filters: [{ field: "sm_site_name", type: "list-facet", value: options.siteList }],

    // The change handler passes the current query- and result state for render
    // as well as the default handlers for interaction with the search component
    onChange: (state, handlers) =>
      // Render the faceted search component
      ReactDOM.render(
        <FederatedSolrFacetedSearch
          {...state}
          {...handlers}
          customComponents={FederatedSolrComponentPack}
          bootstrapCss={false}
          onSelectDoc={() => { }}
          truncateFacetListsAt={-1}
          options={options}
          tagsMapping={getTagsMapping()}
          categoryMapping={getCategoryMapping()}
        />,
        document.getElementById("fs-root")
      )
  });

  // Check if there is a querystring param search term and make initial query.
  searchFromQuerystring(solrClient, options);
  categoryDefaultValue(solrClient, options);

  // Execute query after being modified.
  solrClient.state.query.start = solrClient.state.query.start || 0;
  solrClient.sendQuery(solrClient.state.query);

  // Listen for browser history changes / updated querystring, make new query.
  // See https://developer.mozilla.org/en-US/docs/Web/Events/popstate
  window.onpopstate = function () {
    searchFromQuerystring(solrClient, options);
    categoryDefaultValue(solrClient, options);

    // Execute query after being modified.
    solrClient.state.query.start = solrClient.state.query.start || 0;
    solrClient.sendQuery(solrClient.state.query);
  };
};

// If we are in the production environment (i.e. using the build compiled js)
// @see https://github.com/facebook/create-react-app/blob/master/packages/react-scripts/template/README.md#adding-custom-environment-variables
if (process.env.NODE_ENV === 'production') {
  // Get the root element where the app will be rendered.
  const root = document.getElementById("fs-root");

  if (root) {
    // Get the data attribute which has the stringified configuration data json object.
    if (Object.hasOwnProperty.call(root.dataset, 'federatedSearchAppConfig')) {
      const settings = JSON.parse(root.dataset.federatedSearchAppConfig);
      init(settings);
    }
    else {
      console.error('Federated Search React | Could not find a data-federated-search-app-config attribute on div#root.  Please populate data-federated-search-app-config with search app configuration data.');
    }
  }
  else {
    console.error('Federated Search React | Could not find div#root in which to load the search app.');
  }
}
// This is not production (i.e. not using the build compiled js)
else {
  // Get the local environment settings for the search app and initialize.
  import('./.env.local.js')
    .then(
      (settings) => {
        init(settings); // Load the app, passing in the ./.env.local.js config.
      },
      (error) => {
        console.error('Federated Search React | Could not load local configuration for search app: ', error);
      }
    );
}
